import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface EnvPlaybackSession {
  envId: string;
  podName?: string;
  interviewTabId: string;
  questionId?: string;
  stackLabel?: string;
  startedAt?: number;
  endedAt?: number;
  updatedAt?: number;
  eventCount?: number;
  surfaceCounts?: Record<string, number>;
  captureVersion?: string;
  status?: 'ACTIVE' | 'COMPLETE' | 'PARTIAL' | 'FAILED' | string;
}

export interface EnvPlaybackGap {
  gapId: string;
  envId: string;
  interviewTabId: string;
  startedAt: number;
  endedAt?: number;
  reason?: string;
}

export interface EnvPlaybackEvent {
  eventId: string;
  envId: string;
  interviewTabId: string;
  surface: string;
  type: string;
  timestamp: number;
  sequence: number;
  actorId?: string;
  actorName?: string;
  actorRole?: string;
  actorSource?: string;
  captureSessionId?: string;
  payload?: Record<string, any>;
}

interface PlaybackEnvState {
  sessions: Record<string, EnvPlaybackSession>;
  eventsByEnv: Record<string, EnvPlaybackEvent[]>;
  eventsLoadingByEnv: Record<string, boolean>;
  gapsByEnv: Record<string, EnvPlaybackGap[]>;
  isLoaded: boolean;
}

const initialState: PlaybackEnvState = {
  sessions: {},
  eventsByEnv: {},
  eventsLoadingByEnv: {},
  gapsByEnv: {},
  isLoaded: false,
};

export const playbackEnvSlice = createSlice({
  name: 'playbackEnv',
  initialState,
  reducers: {
    setEnvPlaybackSessions: (
      state,
      action: PayloadAction<Record<string, EnvPlaybackSession> | null>
    ) => {
      state.sessions = action.payload ?? {};
      state.isLoaded = true;
    },
    setEnvPlaybackGaps: (
      state,
      action: PayloadAction<{ envId: string; gaps: EnvPlaybackGap[] }>
    ) => {
      state.gapsByEnv[action.payload.envId] = action.payload.gaps;
    },
    setEnvPlaybackEventsLoading: (
      state,
      action: PayloadAction<{ envId: string; loading: boolean }>
    ) => {
      state.eventsLoadingByEnv[action.payload.envId] = action.payload.loading;
    },
    setEnvPlaybackEvents: (
      state,
      action: PayloadAction<{ envId: string; events: EnvPlaybackEvent[] }>
    ) => {
      state.eventsByEnv[action.payload.envId] = action.payload.events.sort(
        (a, b) =>
          a.timestamp - b.timestamp ||
          a.surface.localeCompare(b.surface) ||
          a.sequence - b.sequence
      );
      state.eventsLoadingByEnv[action.payload.envId] = false;
    },
  },
});

export const {
  setEnvPlaybackSessions,
  setEnvPlaybackGaps,
  setEnvPlaybackEvents,
  setEnvPlaybackEventsLoading,
} = playbackEnvSlice.actions;

export default playbackEnvSlice.reducer;
